import type { CSSProperties, ReactNode } from 'react';
import { useRef } from 'react';

import { useTrackLinks } from '#/hooks/useTrackLinks';
import { colors, textStyles } from '#/theme/theme';
import { getDataAndAriaProps } from '#/utils/getDataAndAriaProps';

import { StyledText } from './Text.styled';
import { useLinksHaveUTMParams } from './useLinksHaveUTMParams';

type Props = {
  id?: string;
  className?: string;
  tag?: string;
  htmlFor?: string;
  styleAs?: keyof typeof textStyles;
  children?: ReactNode;
  dangerouslySetInnerHTML?: { __html: string };
  analyticsData?: {
    eventName: string;
    eventProperties?: Record<string, unknown>;
  };
  title?: string;
  qaId?: string;
  lineClamp?: number;
  // Style shortcuts
  letterSpacing?: number | string;
  fontSize?: number | string;
  fontWeight?: CSSProperties['fontWeight'];
  fontStyle?: CSSProperties['fontStyle'];
  backgroundColor?: string;
  color?: string;
  textAlign?: CSSProperties['textAlign'];
  p?: number | string;
  padding?: number | string;
  py?: number | string;
  pt?: number | string;
  pb?: number | string;
  px?: number | string;
  pr?: number | string;
  pl?: number | string;
  m?: number | string;
  margin?: number | string;
  my?: number | string;
  mb?: number | string;
  marginBottom?: number | string;
  mt?: number | string;
  marginTop?: number | string;
  mx?: number | string;
  ml?: number | string;
  marginLeft?: number | string;
  mr?: number | string;
  marginRight?: number | string;
  lineHeight?: number | string;
  opacity?: number | string;
  display?: CSSProperties['display'];
  verticalAlign?: CSSProperties['verticalAlign'];
  whiteSpace?: CSSProperties['whiteSpace'];
  textOverflow?: CSSProperties['textOverflow'];
  overflow?: CSSProperties['overflow'];
  style?: CSSProperties;
};

export function Text({
  id,
  className,
  tag = 'div',
  htmlFor,
  styleAs,
  children,
  qaId,
  analyticsData,
  dangerouslySetInnerHTML,
  title,
  lineClamp,
  letterSpacing,
  fontSize,
  fontWeight,
  fontStyle,
  backgroundColor,
  color,
  textAlign,
  p,
  padding,
  py,
  pt,
  pb,
  px,
  pl,
  pr,
  m,
  margin,
  my,
  mb,
  marginBottom,
  mt,
  marginTop,
  mx,
  ml,
  marginLeft,
  mr,
  marginRight,
  lineHeight,
  opacity,
  display,
  verticalAlign,
  whiteSpace,
  textOverflow,
  overflow,
  style,
  ...otherProps
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useTrackLinks({
    ref,
    eventName: analyticsData?.eventName,
    eventProperties: analyticsData?.eventProperties,
  });

  useLinksHaveUTMParams({ ref });

  const additionalProps = { ...getDataAndAriaProps(otherProps) };
  if (htmlFor) additionalProps['htmlFor'] = htmlFor;

  const cleanStyles = Object.fromEntries(
    Object.entries({
      letterSpacing,
      fontSize,
      fontWeight,
      fontStyle,
      backgroundColor:
        backgroundColor && backgroundColor in colors
          ? colors[backgroundColor as keyof typeof colors]
          : backgroundColor,
      color:
        color && color in colors ? colors[color as keyof typeof colors] : color,
      textAlign,
      padding: p ?? padding,
      paddingTop: pt ?? py,
      paddingBottom: pb ?? py,
      paddingLeft: pl ?? px,
      paddingRight: pr ?? px,
      margin: m ?? margin,
      marginBottom: mb ?? marginBottom ?? my,
      marginTop: mt ?? marginTop ?? my,
      marginLeft: ml ?? marginLeft ?? mx,
      marginRight: mr ?? marginRight ?? mx,
      lineHeight,
      opacity,
      display,
      verticalAlign,
      whiteSpace,
      textOverflow,
      overflow,
      ...style,
    }).filter(([, value]) => typeof value !== 'undefined'),
  );

  return (
    <StyledText
      // oxlint-disable-next-line react/jsx-props-no-spreading
      {...additionalProps}
      as={tag}
      ref={ref}
      id={id}
      className={className}
      data-qa-id={qaId}
      title={title}
      $styleAs={styleAs}
      $lineClamp={lineClamp}
      style={cleanStyles}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </StyledText>
  );
}
